/**
 * Shared table utility functions for the SOHAndRUL feature.
 */
// Re-export the centralized computed-date formatter so existing imports
// (table cell renderers, CSV export, tests) keep a single source of truth.
export { formatComputedDate } from "../../../../Helper/DatePicker/DateConverter";

/**
 * Flatten an API row from { xLabel, metricsArray, metaInfo } format
 * into a flat object suitable for table rendering.
 */
export function flattenRow(item) {
  const row = { id: item.xLabel, batteryID: item.xLabel };
  (item.metricsArray || []).forEach(({ type, value }) => { row[type] = value; });
  Object.assign(row, item.metaInfo || {});
  return row;
}

/**
 * Narrow a schema's select/metaInfo list to what the current view actually needs.
 *
 * Keeps a schema field when its column is selected, or when it is named in extraFields —
 * fields the table needs regardless of column selection (renderer inputs, the active sort
 * column). Schema order is preserved and unknown names are dropped, so the result is always
 * a subset of what the org meta declares.
 *
 * @param {Array<string>} schemaFields      - org meta `select` or `metaInfo`
 * @param {Array<string>} selectedColumnKeys - dataIndex of every visible column
 * @param {Array<string>} extraFields        - always-request field names
 * @returns {Array<string>}
 */
export function narrowRequestFields(schemaFields, selectedColumnKeys, extraFields = []) {
  const wanted = new Set(
    [...(selectedColumnKeys || []), ...(extraFields || [])].filter(Boolean)
  );
  return (schemaFields || []).filter((field) => wanted.has(field));
}

/**
 * Identity of a battery-list table's current filter set, used to decide when pagination
 * and sort must reset back to page 1 / the default column.
 *
 * Compared by VALUE, not by reference: callers rebuild these objects as they re-render, so
 * a reference check would reset — and refetch — when nothing actually changed.
 *
 * @param {object} globalFilters
 * @param {Array}  additionalFilters
 * @param {string} defaultSortColumn
 * @returns {string}
 */
export function buildTableFiltersKey(globalFilters, additionalFilters, defaultSortColumn) {
  return JSON.stringify({
    globalFilters: globalFilters ?? {},
    additionalFilters: additionalFilters ?? [],
    defaultSortColumn: defaultSortColumn ?? null,
  });
}

/**
 * Build the battery list table title from the org's base-entity (singular) label.
 * "Bus" -> "Bus List", "Vehicle" -> "Vehicle List", "Battery" -> "Battery List".
 * Falls back to "Battery List" when the label is missing / blank.
 * @param {string} entityLabel - baseEntityTypeLable from useBaseEntityInfo()
 * @returns {string}
 */
export function buildBatteryListTitle(entityLabel) {
  const label = typeof entityLabel === "string" ? entityLabel.trim() : "";
  if (!label) return "Battery List";
  return `${label} List`;
}

/**
 * Backend field behind the "Odometer (km)" column on the SOH lists: the odometer captured
 * when SOH was last computed. Deliberately NOT `odometer`, which is the vehicle's current
 * reading (shown separately as "km Covered") — filtering that while displaying this one
 * returned rows whose odometer cell read "-".
 */
export const SOH_ODOMETER_FIELD = "latestSOHPredictionOdometer";

/**
 * Build the odometer range entry for the backend `filters` list, or null when no bound
 * is set. Uses the platform-wide range wire format shared with the generic table
 * filters: a list holding one JSON string of { start, end }. Either bound may be empty,
 * which the backend reads as open-ended.
 *
 * @param {{start: string, end: string}} odometer
 * @returns {{name: string, value: string[]}|null}
 */
export function buildOdometerRangeFilter(odometer) {
  const start = odometer?.start ?? "";
  const end = odometer?.end ?? "";
  if (start === "" && end === "") return null;
  return { name: SOH_ODOMETER_FIELD, value: [JSON.stringify({ start, end })] };
}

/**
 * The first org meta drill-down table node present under BatteryIntelligence, in the
 * order of `metaKeys` (e.g. ["SOHMigrationDrillDown", "SOHDrillDown"]), or undefined.
 * A key may be a dotted path to a nested node ("SOHMigrationDrillDown.pack" reads
 * SOHMigrationDrillDown.pack.table). A node counts as present when it is an object.
 *
 * @param {object|null|undefined} batteryIntelligenceMeta  metaInfo.BatteryIntelligence
 * @param {string[]} metaKeys
 * @returns {object|undefined}  the `<key>.table` node
 */
export function pickDrillDownTableSchema(batteryIntelligenceMeta, metaKeys) {
  const keys = Array.isArray(metaKeys) ? metaKeys : [];
  for (const key of keys) {
    const node = typeof key === "string"
      ? key.split(".").reduce((obj, part) => obj?.[part], batteryIntelligenceMeta)
      : undefined;
    const table = node?.table;
    if (table && typeof table === "object") return table;
  }
  return undefined;
}

/**
 * Drill-down table schema for SOHBreakdownDrillDownModal: the org meta
 * `SOHDrillDown.table` node when present, else the local fallback columns (with an
 * empty select / metaInfo, as before). An optional `schemaFilter` (e.g.
 * filterSohHistoryDrillDownSchema) then narrows it to what the caller's endpoint
 * supports. Without a filter the result is exactly the unfiltered schema.
 *
 * @param {object|null|undefined} tableSchema  org meta { columnsConfig, columnOrdering, select, metaInfo }
 * @param {object} fallbackColumnsConfig
 * @param {string[]} fallbackColumnOrder
 * @param {Function|null} schemaFilter         (schema) => schema
 * @returns {{ columnsConfig: object, columnOrdering: string[], select: string[], metaInfo: string[] }}
 */
export function resolveDrillDownSchema(tableSchema, fallbackColumnsConfig, fallbackColumnOrder, schemaFilter = null) {
  const schema = tableSchema
    ? {
      columnsConfig: tableSchema.columnsConfig || {},
      columnOrdering: tableSchema.columnOrdering || [],
      select: tableSchema.select || [],
      metaInfo: tableSchema.metaInfo || [],
    }
    : {
      columnsConfig: fallbackColumnsConfig || {},
      columnOrdering: fallbackColumnOrder || [],
      select: [],
      metaInfo: [],
    };
  return typeof schemaFilter === "function" ? schemaFilter(schema) : schema;
}

/**
 * Props for the drill-down table's list actions (CSV download, mail, row selection).
 * With hideListActions the download and mail buttons are hidden and row selection
 * (which only feeds those actions) is turned off -- for endpoints with no list-actions.
 *
 * @param {boolean} hideListActions
 * @param {{ getCSVData: Function, onSendEmail: Function, rowSelection: object }} handlers
 */
export function getDrillDownListActionProps(hideListActions, { getCSVData, onSendEmail, rowSelection } = {}) {
  if (hideListActions) {
    return { disableDownloads: true, showEmailComposer: false, rowSelection: undefined };
  }
  return { disableDownloads: false, getCSVData, onSendEmail, rowSelection };
}

/**
 * State-setter helper: returns `prev` when `next` holds the same items in the same
 * order, else `next`. Keeps an array's identity stable across rebuilds so effects
 * that depend on it do not rerun.
 */
export function keepIfSameItems(prev, next) {
  const a = Array.isArray(prev) ? prev : [];
  const b = Array.isArray(next) ? next : [];
  if (a.length === b.length && a.every((v, i) => v === b[i])) return prev;
  return b;
}

/**
 * Row adapter for the shared "operationalStatus" field indicator, whose preset reads
 * tag_battery_status (value) and tag_battery_sub_status (reason). The SOH list endpoints
 * return the status as `operationalStatus` (display name, e.g. "Non-Operational", which
 * the preset matches) and no sub-status, so only the value is copied: the tooltip then
 * shows without a reason.
 * @param {object|null|undefined} record
 * @returns {{ tag_battery_status: string|null }}
 */
export function toOperationalStatusIndicatorRecord(record) {
  return { tag_battery_status: record?.operationalStatus ?? null };
}
